import type { ListingItem } from '@lib/listings/types';

export type HomepageTabColour = 'blue' | 'magenta' | 'green' | 'amber';

/**
 * One filter tab on the homepage, and the label a matching card wears. An
 * editor defines these in the Studio's Homepage document; the defaults below
 * stand in until that document exists.
 */
export type HomepageTab = {
  id: string;
  label: string;
  /** Short singular label for a card, e.g. "Team reveal" under a "Team reveals" tab. */
  cardLabel: string;
  colour: HomepageTabColour;
  categorySlugs: string[];
  /** WordPress primary category names, until WordPress is switched off (FFH-3025). */
  wordPressCategoryNames: string[];
  includesTeamReveals: boolean;
  /** Claims every piece that no other tab matches. */
  isCatchAll: boolean;
};

export const ALL_TAB_ID = 'all';
export const ALL_TAB_LABEL = 'All';

export const DEFAULT_HOMEPAGE_TABS: HomepageTab[] = [
  {
    id: 'fpl-tips',
    label: 'FPL tips',
    cardLabel: 'FPL tips',
    colour: 'blue',
    categorySlugs: [],
    wordPressCategoryNames: ['Fantasy Premier League', 'FPL Captain'],
    includesTeamReveals: false,
    isCatchAll: true
  },
  {
    id: 'team-reveals',
    label: 'Team reveals',
    cardLabel: 'Team reveal',
    colour: 'magenta',
    categorySlugs: ['team-reveals'],
    wordPressCategoryNames: ['Team Reveal'],
    includesTeamReveals: true,
    isCatchAll: false
  },
  {
    id: 'team-news',
    label: 'Team news',
    cardLabel: 'Team news',
    colour: 'green',
    categorySlugs: ['predicted-lineups', 'news'],
    // WordPress files the lineups and press conference posts under this name.
    wordPressCategoryNames: ['Fantasy Football'],
    includesTeamReveals: false,
    isCatchAll: false
  },
  {
    id: 'sun-dream-team',
    label: 'Sun Dream Team',
    cardLabel: 'Sun Dream Team',
    colour: 'amber',
    categorySlugs: ['sun-dream-team'],
    wordPressCategoryNames: ['Sun Dream Team'],
    includesTeamReveals: false,
    isCatchAll: false
  }
];

const normaliseName = (name: string): string => name.trim().toLowerCase();

const matchesTabExplicitly = (item: ListingItem, tab: HomepageTab): boolean => {
  if (tab.includesTeamReveals && item.isTeamReveal) return true;
  if (item.categorySlug && tab.categorySlugs.includes(item.categorySlug)) {
    return true;
  }
  if (item.source === 'wordpress' && item.categoryName) {
    const name = normaliseName(item.categoryName);
    return tab.wordPressCategoryNames.some(
      (candidate) => normaliseName(candidate) === name
    );
  }
  return false;
};

/** The first tab that claims the item, falling back to the catch-all tab. */
export const tabForItem = (
  item: ListingItem,
  tabs: HomepageTab[]
): HomepageTab | undefined =>
  tabs.find((tab) => matchesTabExplicitly(item, tab)) ??
  tabs.find((tab) => tab.isCatchAll);

export const filterItemsByTab = (
  items: ListingItem[],
  tabs: HomepageTab[],
  activeTabId: string
): ListingItem[] => {
  if (activeTabId === ALL_TAB_ID) return items;
  return items.filter((item) => tabForItem(item, tabs)?.id === activeTabId);
};

/** Only tabs with something behind them are worth showing. */
export const tabsWithItems = (
  items: ListingItem[],
  tabs: HomepageTab[]
): HomepageTab[] =>
  tabs.filter((tab) =>
    items.some((item) => tabForItem(item, tabs)?.id === tab.id)
  );

export type CardLabel = { text: string; colour: HomepageTabColour };

const FALLBACK_CARD_LABEL_COLOUR: HomepageTabColour = 'blue';
const FALLBACK_CARD_LABEL_TEXT = 'FPL tips';

/**
 * A card wears the short label of the tab it sits under rather than its fine
 * CMS category, so the grid reads as a handful of consistent strands.
 */
export const cardLabelFor = (
  item: ListingItem,
  tabs: HomepageTab[]
): CardLabel => {
  const tab = tabForItem(item, tabs);
  if (tab) return { text: tab.cardLabel, colour: tab.colour };
  return {
    text: item.categoryName ?? FALLBACK_CARD_LABEL_TEXT,
    colour: FALLBACK_CARD_LABEL_COLOUR
  };
};
